import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Search, Save } from "lucide-react";

const EMPTY = {
  name: "", isin: "", category: "Equity", sub_category: "",
  fund_manager: "",
  rolling_returns_5yr_in_10yr: { average_pct: "", min_pct: "", max_pct: "" },
  sharpe_ratio: "", standard_deviation: "", pct_periods_returns_above_20: "",
  aum: "", data_as_of_date: "",
  market_cap_allocation: { large_pct: "", mid_pct: "", small_pct: "" },
  sector_allocation: {
    IT_pct: "", Banking_pct: "", NBFC_pct: "", Metals_pct: "", FMCG_pct: "",
    Auto_Ancillaries_pct: "", Finance_pct: "", Insurance_pct: "", Other_pct: "",
  },
  trailing_returns: { return_1yr_pct: "", return_3yr_pct: "", return_5yr_pct: "" },
};

export default function MutualFundResearch() {
  const [funds, setFunds] = useState([]);
  const [cats, setCats] = useState([]);
  const [filterCat, setFilterCat] = useState("");
  const [filterSub, setFilterSub] = useState("");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);      // fund being edited (null when dialog closed)
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: fs }, { data: cs }] = await Promise.all([
        api.get("/mutual-funds", { params: { category: filterCat || undefined, sub_category: filterSub || undefined, q: q || undefined } }),
        api.get("/mf-categories"),
      ]);
      setFunds(fs);
      setCats(cs);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Load failed");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterCat, filterSub]);

  const subOptions = useMemo(() => {
    const c = cats.find((c) => c.category === form.category);
    return c?.sub_categories || [];
  }, [cats, form.category]);

  const filterSubOptions = useMemo(() => {
    const c = cats.find((c) => c.category === filterCat);
    return c?.sub_categories || [];
  }, [cats, filterCat]);

  const openNew = () => { setEditing({}); setForm(EMPTY); };
  const openEdit = (f) => {
    setEditing(f);
    setForm({
      ...EMPTY, ...f,
      rolling_returns_5yr_in_10yr: f.rolling_returns_5yr_in_10yr || EMPTY.rolling_returns_5yr_in_10yr,
      market_cap_allocation: f.market_cap_allocation || EMPTY.market_cap_allocation,
      sector_allocation: { ...EMPTY.sector_allocation, ...(f.sector_allocation || {}) },
      trailing_returns: f.trailing_returns || EMPTY.trailing_returns,
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        sharpe_ratio: Number(form.sharpe_ratio) || null,
        standard_deviation: Number(form.standard_deviation) || null,
        pct_periods_returns_above_20: Number(form.pct_periods_returns_above_20) || null,
        aum: Number(form.aum) || null,
        rolling_returns_5yr_in_10yr: {
          average_pct: Number(form.rolling_returns_5yr_in_10yr.average_pct) || null,
          min_pct: Number(form.rolling_returns_5yr_in_10yr.min_pct) || null,
          max_pct: Number(form.rolling_returns_5yr_in_10yr.max_pct) || null,
        },
        market_cap_allocation: {
          large_pct: Number(form.market_cap_allocation.large_pct) || 0,
          mid_pct: Number(form.market_cap_allocation.mid_pct) || 0,
          small_pct: Number(form.market_cap_allocation.small_pct) || 0,
        },
        sector_allocation: Object.fromEntries(
          Object.entries(form.sector_allocation).map(([k, v]) => [k, Number(v) || 0])
        ),
        trailing_returns: {
          return_1yr_pct: form.trailing_returns.return_1yr_pct === "" ? null : Number(form.trailing_returns.return_1yr_pct),
          return_3yr_pct: form.trailing_returns.return_3yr_pct === "" ? null : Number(form.trailing_returns.return_3yr_pct),
          return_5yr_pct: form.trailing_returns.return_5yr_pct === "" ? null : Number(form.trailing_returns.return_5yr_pct),
        },
      };
      if (editing?.id) await api.patch(`/mutual-funds/${editing.id}`, payload);
      else await api.post("/mutual-funds", payload);
      toast.success("Saved");
      setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete ${f.name}?`)) return;
    try {
      await api.delete(`/mutual-funds/${f.id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline mb-1 text-muted-foreground">Admin only · Research</p>
          <h1 className="font-dashboard text-3xl font-bold tracking-tight">Mutual Fund Research</h1>
          <p className="mt-1 text-sm text-muted-foreground">Curated fund database with performance analytics. Only the Principal Advisor can browse this.</p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="add-fund-btn"><Plus className="h-4 w-4" /> Add fund</Button>
      </div>

      <Card className="border-border bg-white p-4">
        <div className="grid gap-3 md:grid-cols-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search name" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()} data-testid="mf-search-input" />
          </div>
          <select className="h-10 rounded-md border border-input bg-background px-3 text-sm" value={filterCat}
            onChange={(e) => { setFilterCat(e.target.value); setFilterSub(""); }} data-testid="filter-category">
            <option value="">All categories</option>
            {cats.map((c) => <option key={c.category}>{c.category}</option>)}
          </select>
          <select className="h-10 rounded-md border border-input bg-background px-3 text-sm" value={filterSub}
            onChange={(e) => setFilterSub(e.target.value)} disabled={!filterCat} data-testid="filter-subcategory">
            <option value="">All sub-categories</option>
            {filterSubOptions.map((s) => <option key={s}>{s}</option>)}
          </select>
          <Button variant="outline" onClick={load} data-testid="apply-filter-btn">Apply filter</Button>
        </div>
      </Card>

      <Card className="border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px] text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Fund</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">5yr rolling (avg / min / max)</th>
                <th className="px-4 py-3">Sharpe</th>
                <th className="px-4 py-3">SD</th>
                <th className="px-4 py-3">% &gt;20%</th>
                <th className="px-4 py-3">AUM (₹cr)</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="mf-table-body">
              {funds.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-16 text-center text-muted-foreground">No funds match. Add your first one.</td></tr>
              ) : funds.map((f) => {
                const rr = f.rolling_returns_5yr_in_10yr || {};
                return (
                  <tr key={f.id} className="border-b border-border last:border-0 hover:bg-secondary/20" data-testid={`fund-row-${f.id}`}>
                    <td className="px-4 py-4">
                      <div className="font-medium">{f.name}</div>
                      <div className="text-xs text-muted-foreground">{f.fund_manager || "—"} {f.isin && `· ${f.isin}`}</div>
                    </td>
                    <td className="px-4 py-4 text-xs">{f.category}<br /><span className="text-muted-foreground">{f.sub_category}</span></td>
                    <td className="px-4 py-4 text-xs">
                      {rr.average_pct != null ? `${rr.average_pct}%` : "—"}
                      <span className="text-muted-foreground"> / {rr.min_pct ?? "—"}% / {rr.max_pct ?? "—"}%</span>
                    </td>
                    <td className="px-4 py-4 text-xs">{f.sharpe_ratio ?? "—"}</td>
                    <td className="px-4 py-4 text-xs">{f.standard_deviation ?? "—"}</td>
                    <td className="px-4 py-4 text-xs">{f.pct_periods_returns_above_20 ?? "—"}%</td>
                    <td className="px-4 py-4 text-xs">{f.aum ?? "—"}</td>
                    <td className="px-4 py-4 text-right">
                      <Button size="sm" variant="outline" onClick={() => openEdit(f)} data-testid={`edit-fund-${f.id}`}>Edit</Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(f)} className="ml-1" data-testid={`delete-fund-${f.id}`}><Trash2 className="h-3 w-3" /></Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={editing !== null} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit fund" : "Add fund"}</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-3 md:grid-cols-2">
            <div className="md:col-span-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="fund-name-input" /></div>
            <div><Label>Category</Label>
              <select className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value, sub_category: "" })} data-testid="fund-category-select">
                {cats.map((c) => <option key={c.category}>{c.category}</option>)}
              </select>
            </div>
            <div><Label>Sub-category</Label>
              <select className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.sub_category} onChange={(e) => setForm({ ...form, sub_category: e.target.value })} data-testid="fund-subcategory-select">
                <option value=""></option>
                {subOptions.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div><Label>ISIN</Label><Input value={form.isin || ""} onChange={(e) => setForm({ ...form, isin: e.target.value })} /></div>
            <div><Label>Fund Manager</Label><Input value={form.fund_manager || ""} onChange={(e) => setForm({ ...form, fund_manager: e.target.value })} /></div>
            <div className="md:col-span-2 grid grid-cols-3 gap-2">
              <div><Label>Rolling 5y — avg %</Label><Input type="number" step="0.01" value={form.rolling_returns_5yr_in_10yr.average_pct} onChange={(e) => setForm({ ...form, rolling_returns_5yr_in_10yr: { ...form.rolling_returns_5yr_in_10yr, average_pct: e.target.value } })} /></div>
              <div><Label>min %</Label><Input type="number" step="0.01" value={form.rolling_returns_5yr_in_10yr.min_pct} onChange={(e) => setForm({ ...form, rolling_returns_5yr_in_10yr: { ...form.rolling_returns_5yr_in_10yr, min_pct: e.target.value } })} /></div>
              <div><Label>max %</Label><Input type="number" step="0.01" value={form.rolling_returns_5yr_in_10yr.max_pct} onChange={(e) => setForm({ ...form, rolling_returns_5yr_in_10yr: { ...form.rolling_returns_5yr_in_10yr, max_pct: e.target.value } })} /></div>
            </div>
            <div><Label>Sharpe ratio</Label><Input type="number" step="0.01" value={form.sharpe_ratio} onChange={(e) => setForm({ ...form, sharpe_ratio: e.target.value })} /></div>
            <div><Label>Std. deviation</Label><Input type="number" step="0.01" value={form.standard_deviation} onChange={(e) => setForm({ ...form, standard_deviation: e.target.value })} /></div>
            <div><Label>% periods &gt;20%</Label><Input type="number" step="0.01" value={form.pct_periods_returns_above_20} onChange={(e) => setForm({ ...form, pct_periods_returns_above_20: e.target.value })} /></div>
            <div><Label>AUM (₹cr)</Label><Input type="number" step="0.01" value={form.aum} onChange={(e) => setForm({ ...form, aum: e.target.value })} /></div>
            <div className="md:col-span-2"><Label>Data as of</Label><Input type="date" value={form.data_as_of_date || ""} onChange={(e) => setForm({ ...form, data_as_of_date: e.target.value })} /></div>

            <div className="md:col-span-2 mt-2 border-t border-border pt-3">
              <div className="overline mb-2 text-muted-foreground">Portfolio composition — used by the Overlap tool</div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-xs">Large cap %</Label><Input type="number" step="0.01" value={form.market_cap_allocation.large_pct} onChange={(e) => setForm({ ...form, market_cap_allocation: { ...form.market_cap_allocation, large_pct: e.target.value } })} data-testid="fund-large-pct" /></div>
                <div><Label className="text-xs">Mid cap %</Label><Input type="number" step="0.01" value={form.market_cap_allocation.mid_pct} onChange={(e) => setForm({ ...form, market_cap_allocation: { ...form.market_cap_allocation, mid_pct: e.target.value } })} data-testid="fund-mid-pct" /></div>
                <div><Label className="text-xs">Small cap %</Label><Input type="number" step="0.01" value={form.market_cap_allocation.small_pct} onChange={(e) => setForm({ ...form, market_cap_allocation: { ...form.market_cap_allocation, small_pct: e.target.value } })} data-testid="fund-small-pct" /></div>
              </div>
            </div>

            <div className="md:col-span-2">
              <div className="overline mb-2 text-muted-foreground">Sector allocation %</div>
              <div className="grid grid-cols-3 gap-2">
                {Object.keys(form.sector_allocation).map((k) => (
                  <div key={k}>
                    <Label className="text-xs">{k.replace("_pct", "").replace(/_/g, " ")}</Label>
                    <Input type="number" step="0.01"
                      value={form.sector_allocation[k]}
                      onChange={(e) => setForm({ ...form, sector_allocation: { ...form.sector_allocation, [k]: e.target.value } })}
                      data-testid={`fund-sector-${k}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="md:col-span-2 mt-2 border-t border-border pt-3">
              <div className="overline mb-2 text-muted-foreground">Trailing returns — used by the Underperformance tool</div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-xs">1Y %</Label><Input type="number" step="0.01" value={form.trailing_returns.return_1yr_pct} onChange={(e) => setForm({ ...form, trailing_returns: { ...form.trailing_returns, return_1yr_pct: e.target.value } })} data-testid="fund-r1y" /></div>
                <div><Label className="text-xs">3Y %</Label><Input type="number" step="0.01" value={form.trailing_returns.return_3yr_pct} onChange={(e) => setForm({ ...form, trailing_returns: { ...form.trailing_returns, return_3yr_pct: e.target.value } })} data-testid="fund-r3y" /></div>
                <div><Label className="text-xs">5Y %</Label><Input type="number" step="0.01" value={form.trailing_returns.return_5yr_pct} onChange={(e) => setForm({ ...form, trailing_returns: { ...form.trailing_returns, return_5yr_pct: e.target.value } })} data-testid="fund-r5y" /></div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={busy || !form.name} className="gap-2" data-testid="fund-save-btn"><Save className="h-4 w-4" /> {busy ? "Saving…" : "Save fund"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
